import axios from 'axios';

import { CIError, ErrorSource, GraphQLErrorCode } from 'common/error/error_util';
import { getSessionIdToken } from 'common/graphql/client';
import { CILogger } from 'common/log/log.provider';

import type { FxTokenResponse } from './fx.types';

interface TokenCache {
    token: string;
    expiresAt: number;
}

let tokenCache: TokenCache | null = null;
// In-flight deduplication: parallel requests share one token exchange instead of each firing their own.
let inflightTokenRequest: Promise<string> | null = null;

async function exchangeOktaIdTokenForMsulAccessToken(): Promise<string> {
    const baseUrl = process.env.REACT_APP_FX_URL;

    if (!baseUrl) {
        throw new CIError('FX MSUL base URL not configured', GraphQLErrorCode.SYSTEM_ERROR, ErrorSource.REST, null);
    }

    const idToken = await getSessionIdToken();

    if (!idToken) {
        CILogger.getInstance().error('FX MSUL: session token is missing — Okta session may have expired', { baseUrl });
        throw new CIError('FX MSUL: session token unavailable', GraphQLErrorCode.SYSTEM_ERROR, ErrorSource.OKTA, null);
    }

    try {
        const response = await axios.post<FxTokenResponse>(
            `${baseUrl}/api/oauth2/token`,
            new URLSearchParams({
                grant_type: 'urn:ietf:params:oauth:grant-type:token-exchange',
                subject_token: idToken,
                subject_token_type: 'urn:ietf:params:oauth:token-type:id_token',
                scope: 'msul',
            }).toString(),
            {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            }
        );

        const expiresIn = response.data.expires_in;
        tokenCache = {
            token: response.data.access_token,
            expiresAt: Date.now() + expiresIn * 1000,
        };

        return tokenCache.token;
    } catch (error) {
        CILogger.getInstance().error('FX MSUL: token exchange failed', error);
        throw new CIError('FX MSUL token exchange failed', GraphQLErrorCode.SYSTEM_ERROR, ErrorSource.REST, error);
    }
}

async function getAccessToken(): Promise<string> {
    if (tokenCache && Date.now() < tokenCache.expiresAt - 30_000) {
        return tokenCache.token;
    }

    if (inflightTokenRequest === null) {
        inflightTokenRequest = exchangeOktaIdTokenForMsulAccessToken().finally(() => {
            inflightTokenRequest = null;
        });
    }

    return inflightTokenRequest;
}

/** Clears the cached MSUL access token — call on logout. */
export function clearFxMsulTokenCache(): void {
    tokenCache = null;
    inflightTokenRequest = null;
}

export const fxMsulClient = axios.create({
    baseURL: process.env.REACT_APP_FX_URL,
    timeout: 15000,
    headers: {
        accept: 'application/json',
        'content-type': 'application/json',
    },
});

fxMsulClient.interceptors.request.use(async (config) => {
    config.headers.Authorization = `Bearer ${await getAccessToken()}`;
    return config;
});

fxMsulClient.interceptors.response.use(
    (response) => response,
    (error) => {
        // Extract FX error detail for meaningful error messages.
        // FX rate-limit / business error body: { message }
        const data = (error as { response?: { data?: { detail?: string; message?: string } } })?.response?.data;
        const serverProvidedMessage = data?.detail ?? data?.message;
        const message = serverProvidedMessage ?? 'FX MSUL request failed';
        CILogger.getInstance().error('FX MSUL: request failed', error);
        // Use 'fx:server_message' as a sentinel key so callers can display error.message directly.
        const translationKey = serverProvidedMessage ? 'fx:server_message' : GraphQLErrorCode.SYSTEM_ERROR;
        return Promise.reject(new CIError(message, translationKey, ErrorSource.REST, error));
    }
);
